import React from 'react';
import PropTypes from 'prop-types';

import {
  Row,
  Col,
  Button,
  Space,
  Alert,
  Table,
  Image,
} from 'antd';
import { EditOutlined, NotificationOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import config from '../../config';
import useList from '../../api/hooks/useList';

const ListView = (props) => {
  const {
    serverPath,
    clientPath,
    title,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const fullPath = React.useMemo(() => `v2/component_types/${serverPath}`, [serverPath]);

  const [loadConfig, setLoadConfig] = React.useState({
    pageIndex: 0,
    pageSize: 20,
    filter: {},
  });

  const {
    load,
    loading,
    data,
    error,
  } = useList(fullPath);

  React.useEffect(() => {
    load(loadConfig.pageIndex, loadConfig.pageSize, loadConfig.filter);
  }, [load, loadConfig, fullPath]);

  const renderError = () => (
    <Row style={{ height: '100%' }} justify="center" align="middle">
      <Col>
        <Alert message={JSON.stringify(error)} type="error" showIcon />
      </Col>
    </Row>
  );

  const handleTableChange = React.useCallback((page) => {
    setLoadConfig({
      ...loadConfig,
      pageIndex: page.current - 1,
      pageSize: page.pageSize,
    });
  }, [setLoadConfig, loadConfig]);

  if (error) {
    return renderError();
  }

  return (
    <Space size="large" style={{ width: '100%' }} direction="vertical">
      <Row justify="space-between">
        <Col>
          <h1>{title}</h1>
        </Col>
        <Col>
          <Button
            onClick={() => navigate(`/componentTypes/${clientPath}/new`)}
            type="primary"
          >
            {t('commons.actions.addNew')}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col flex={1}>
          <Table
            loading={loading}
            dataSource={data ? data.list : []}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
            pagination={{
              current: loadConfig.pageIndex + 1,
              pageSize: loadConfig.pageSize,
              total: data ? data.count : 0,
            }}
          >
            <Table.Column
              title={t('componentTypes.table.image')}
              dataIndex="image"
              key="image"
              render={(value) => (
                <Image width={100} src={`${config.baseApiUrl}v1/images/${value.id}`} />
              )}
            />
            <Table.Column
              title={t('componentTypes.table.name')}
              dataIndex="name"
              key="name"
            />
            <Table.Colum
              title={t('componentTypes.table.actions')}
              key="action"
              render={(text, record) => (
                <Space size="middle">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/componentTypes/${clientPath}/${record.id}`)}
                  />
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<NotificationOutlined />}
                    onClick={() => navigate(`/componentTypes/${clientPath}/${record.id}/notifications`)}
                  />
                </Space>
              )}
            />
          </Table>
        </Col>
      </Row>
    </Space>
  );
};

const propTypes = {
  title: PropTypes.string.isRequired,
  serverPath: PropTypes.string.isRequired,
  clientPath: PropTypes.string.isRequired,
};

ListView.propTypes = propTypes;

export default ListView;
